import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Ban, HelpCircle, Loader2, RefreshCw, RotateCcw, X, XCircle } from "lucide-react";
import { globalBansApi, proposalsApi } from "./api";
import type {
  GlobalBan,
  GlobalBanProposal,
  IncidentCategory,
  ProposalStatus,
} from "./types";
import { StatusBadge } from "../applications/shared/StatusBadge";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { Section, Field, Link } from "../applications/shared/InfoBlock";
import { formatApplicantName, formatDate, formatDateTime } from "../applications/shared/format";
import { personDetail, personName, rutTailLabel } from "./shared/person";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import {
  INCIDENT_CATEGORY_LABELS,
  PROPOSAL_SOURCE_LABELS,
  PROPOSAL_STATUS_LABELS,
  proposalStatusTone,
} from "./shared/labels";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { cn } from "@/lib/utils/cn";

type Tab = "proposals" | "active";

const PROPOSAL_STATUS_OPTIONS: { value: ProposalStatus; label: string }[] = [
  { value: "pending_review", label: "Pendientes de revisión" },
  { value: "approved", label: "Aprobados" },
  { value: "rejected", label: "Rechazados" },
  { value: "requested_more_info", label: "Pidió más info" },
];

export function BlacklistScreen() {
  const [tab, setTab] = useState<Tab>("proposals");

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Governance · Blacklist</p>
          <h1 className="nx-h1">Lista negra global</h1>
          <p className="text-sm text-fg-muted mt-1">
            Ban global = bloquea ingreso a TODOS los eventos de TODOS los clubes
            de la red. Solo se crea aprobando una postulación con evidencia.
          </p>
        </div>
        <div className="inline-flex bg-bg-subtle border border-border rounded-md p-0.5">
          {(
            [
              { value: "proposals", label: "Cola de revisión" },
              { value: "active", label: "Bans activos" },
            ] as const
          ).map((opt) => (
            <button
              key={opt.value}
              onClick={() => setTab(opt.value as Tab)}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-sm transition-colors",
                tab === opt.value
                  ? "bg-bg-card text-fg shadow-nx-sm"
                  : "text-fg-muted hover:text-fg",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </header>

      {tab === "proposals" ? <ProposalsPane /> : <ActiveBansPane />}
    </div>
  );
}

// ── Pane: Proposals ──────────────────────────────────────────────────
function ProposalsPane() {
  const [status, setStatus] = useState<ProposalStatus>("pending_review");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const listQuery = useQuery({
    queryKey: ["governance", "proposals", { status }],
    queryFn: () => proposalsApi.list({ status }),
  });

  const detailQuery = useQuery({
    queryKey: ["governance", "proposals", "detail", selectedId],
    queryFn: () => proposalsApi.detail(selectedId!),
    enabled: selectedId !== null,
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as ProposalStatus)}
          className="nx-input w-auto"
        >
          {PROPOSAL_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button
          onClick={() => listQuery.refetch()}
          className="nx-btn-secondary"
          disabled={listQuery.isFetching}
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </div>

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando postulaciones…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable<GlobalBanProposal>
          rows={listQuery.data ?? []}
          columns={proposalColumns}
          page={1}
          pageSize={(listQuery.data ?? []).length || 1}
          total={(listQuery.data ?? []).length}
          onPageChange={() => {}}
          onRowClick={(r) => setSelectedId(r.id)}
          selectedId={selectedId}
          emptyLabel="No hay postulaciones que coincidan."
        />
      )}

      {selectedId !== null && (
        <ProposalDrawer
          loading={detailQuery.isLoading}
          data={detailQuery.data ?? null}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

const proposalColumns: Column<GlobalBanProposal>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
    width: "min",
  },
  {
    key: "user",
    header: "Persona propuesta",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{personName(r.user, r.subject)}</div>
        <div className="text-2xs text-fg-subtle break-all">{personDetail(r.user, r.subject)}</div>
      </div>
    ),
  },
  {
    key: "category",
    header: "Categoría",
    cell: (r) => (
      <span className="text-fg text-xs">
        {r.incident?.category ? INCIDENT_CATEGORY_LABELS[r.incident.category] : "—"}
      </span>
    ),
  },
  {
    key: "club",
    header: "Postulado por",
    cell: (r) => (
      <div>
        <div className="text-xs text-fg">{r.proposedByClub?.name || r.proposedByClub?.nombre || "—"}</div>
        <div className="text-2xs text-fg-subtle">{PROPOSAL_SOURCE_LABELS[r.source]}</div>
      </div>
    ),
  },
  {
    key: "status",
    header: "Estado",
    cell: (r) => (
      <StatusBadge
        status={r.status}
        tone={proposalStatusTone(r.status)}
        label={PROPOSAL_STATUS_LABELS[r.status]}
      />
    ),
    width: "min",
  },
  {
    key: "createdAt",
    header: "Postulado",
    cell: (r) => <span className="text-fg-subtle text-xs">{formatDate(r.createdAt)}</span>,
    width: "min",
    align: "right",
  },
];

function ProposalDrawer({
  loading,
  data,
  onClose,
}: {
  loading: boolean;
  data: GlobalBanProposal | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { push } = useToast();

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["governance", "proposals"] });
    qc.invalidateQueries({ queryKey: ["governance", "global-bans"] });
  };

  const approve = useMutation({
    mutationFn: (body: {
      decisionReason: string;
      expiresAt: string | null;
      appealable: boolean;
      publicReason?: string;
      category?: IncidentCategory;
    }) => proposalsApi.approve(data!.id, body),
    onSuccess: () => {
      push({ kind: "success", message: "Postulación aprobada. Global ban creado." });
      invalidate();
      onClose();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || "No se pudo aprobar." }),
  });

  const reject = useMutation({
    mutationFn: (decisionReason: string) => proposalsApi.reject(data!.id, decisionReason),
    onSuccess: () => {
      push({ kind: "success", message: "Postulación rechazada." });
      invalidate();
      onClose();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || "No se pudo rechazar." }),
  });

  const reqInfo = useMutation({
    mutationFn: (requestedInfo: string) => proposalsApi.requestMoreInfo(data!.id, requestedInfo),
    onSuccess: () => {
      push({ kind: "success", message: "Solicitud enviada al productor." });
      invalidate();
      onClose();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || "No se pudo pedir info." }),
  });

  // Estados terminales — sólo vista
  const isPending = data?.status === "pending_review" || data?.status === "requested_more_info";

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[600px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="nx-h2 truncate">Postulación a ban global</h2>
            {data && (
              <p className="text-2xs text-fg-subtle mt-0.5">
                #{data.id} · {formatDateTime(data.createdAt)}
              </p>
            )}
          </div>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading || !data ? (
            <div className="flex items-center justify-center py-12 text-fg-subtle">
              <Loader2 size={16} className="animate-spin mr-2" />
              Cargando…
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge
                  status={data.status}
                  tone={proposalStatusTone(data.status)}
                  label={PROPOSAL_STATUS_LABELS[data.status]}
                />
                <span className="text-2xs text-fg-subtle">
                  Origen: {PROPOSAL_SOURCE_LABELS[data.source]}
                </span>
              </div>

              <Section title="Persona propuesta">
                <Field label="Nombre" value={personName(data.user, data.subject)} wide />
                <Field
                  label="Email"
                  value={
                    data.user?.email ? (
                      <a className="text-accent hover:underline" href={`mailto:${data.user.email}`}>
                        {data.user.email}
                      </a>
                    ) : "—"
                  }
                  wide
                />
                <Field label="User ID" value={data.user?.id ?? "—"} />
                <Field label="RUT" value={rutTailLabel(data.subject) ?? "—"} />
              </Section>

              <Section title="Postulado por">
                <Field
                  label="Club"
                  value={data.proposedByClub?.name || data.proposedByClub?.nombre || "—"}
                  wide
                />
                {data.proposedByUser && (
                  <Field
                    label="Usuario que postuló"
                    value={`${formatApplicantName(data.proposedByUser)} <${data.proposedByUser.email}>`}
                    wide
                  />
                )}
              </Section>

              {data.incident && (
                <Section title="Incidente referencia">
                  <Field
                    label="Categoría"
                    value={
                      data.incident.category
                        ? INCIDENT_CATEGORY_LABELS[data.incident.category]
                        : "—"
                    }
                  />
                  <Field label="Severidad" value={data.incident.severity} />
                  <Field
                    label="Descripción"
                    value={
                      data.incident.description ? (
                        <p className="whitespace-pre-wrap text-fg">{data.incident.description}</p>
                      ) : "—"
                    }
                    wide
                  />
                  <Field label="Denunciante" value={data.incident.reporterName ?? "—"} />
                  <Field label="Tel. denunciante" value={data.incident.reporterPhone ?? "—"} />
                  <Field label="Email denunciante" value={data.incident.reporterEmail ?? "—"} wide />
                  <Field
                    label="N° denuncia policial"
                    value={data.incident.policeReportNumber ?? "—"}
                    wide
                  />
                  {data.incident.evidenceUrls && data.incident.evidenceUrls.length > 0 && (
                    <Field
                      label={`Evidencia (${data.incident.evidenceUrls.length})`}
                      value={
                        <div className="flex flex-col gap-1">
                          {data.incident.evidenceUrls.map((url, i) => (
                            <Link key={i} href={url}>
                              Archivo {i + 1}
                            </Link>
                          ))}
                        </div>
                      }
                      wide
                    />
                  )}
                </Section>
              )}

              {data.status !== "pending_review" && (
                <Section title="Decisión Nexus">
                  <Field label="Revisado por" value={data.nexusReviewedBy?.email ?? "—"} />
                  <Field label="Cuándo" value={formatDateTime(data.nexusReviewedAt)} />
                  <Field
                    label="Motivo"
                    value={
                      data.nexusDecisionReason ? (
                        <p className="whitespace-pre-wrap text-fg">{data.nexusDecisionReason}</p>
                      ) : "—"
                    }
                    wide
                  />
                  {data.nexusRequestedInfo && (
                    <Field
                      label="Info pedida al productor"
                      value={<p className="whitespace-pre-wrap text-fg">{data.nexusRequestedInfo}</p>}
                      wide
                    />
                  )}
                </Section>
              )}
            </div>
          )}
        </div>

        {data && isPending && (
          <footer className="px-5 py-3 border-t border-border bg-bg-surface flex flex-wrap items-center gap-2 justify-end">
            <button
              onClick={async () => {
                const info = await confirmAction({
                  title: "Pedir más información al productor",
                  promptLabel: "¿Qué necesitás? (queda en el email al club)",
                  confirmLabel: "Solicitar",
                });
                if (info !== null && (info as string).trim()) reqInfo.mutate((info as string).trim());
              }}
              disabled={reqInfo.isPending || approve.isPending || reject.isPending}
              className="nx-btn-secondary"
            >
              <HelpCircle size={14} /> Pedir más info
            </button>
            <button
              onClick={async () => {
                const reason = await confirmAction({
                  title: "Rechazar postulación",
                  message: "No se crea el ban global. La decisión queda registrada.",
                  promptLabel: "Motivo (obligatorio)",
                  confirmLabel: "Rechazar",
                  tone: "danger",
                });
                if (reason !== null && (reason as string).trim()) reject.mutate((reason as string).trim());
              }}
              disabled={reject.isPending || approve.isPending}
              className="nx-btn-danger"
            >
              <XCircle size={14} /> Rechazar
            </button>
            <button
              onClick={async () => {
                const reason = await confirmAction({
                  title: "Aprobar postulación",
                  message:
                    "Se creará un BAN GLOBAL con duración 12 meses, apelable. El usuario será notificado y bloqueado en TODOS los clubes.",
                  promptLabel: "Motivo de la decisión (obligatorio, queda en audit log)",
                  confirmLabel: "Aprobar y crear ban",
                  tone: "success",
                });
                if (reason !== null && (reason as string).trim()) {
                  const expiresAt = new Date();
                  expiresAt.setMonth(expiresAt.getMonth() + 12);
                  approve.mutate({
                    decisionReason: (reason as string).trim(),
                    expiresAt: expiresAt.toISOString(),
                    appealable: true,
                  });
                }
              }}
              disabled={approve.isPending}
              className="nx-btn-primary"
            >
              <Ban size={14} /> Aprobar y crear ban
            </button>
          </footer>
        )}
      </aside>
    </>
  );
}

// ── Pane: Active Bans ────────────────────────────────────────────────
function ActiveBansPane() {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const listQuery = useQuery({
    queryKey: ["governance", "global-bans"],
    queryFn: () => globalBansApi.list(),
  });

  const selected = useMemo(
    () =>
      selectedId !== null ? (listQuery.data ?? []).find((b) => b.id === selectedId) ?? null : null,
    [listQuery.data, selectedId],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end">
        <button
          onClick={() => listQuery.refetch()}
          className="nx-btn-secondary"
          disabled={listQuery.isFetching}
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </div>

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando bans activos…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable<GlobalBan>
          rows={listQuery.data ?? []}
          columns={banColumns}
          page={1}
          pageSize={(listQuery.data ?? []).length || 1}
          total={(listQuery.data ?? []).length}
          onPageChange={() => {}}
          onRowClick={(r) => setSelectedId(r.id)}
          selectedId={selectedId}
          emptyLabel="No hay bans activos."
        />
      )}

      {selected && <BanDrawer data={selected} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

const banColumns: Column<GlobalBan>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
    width: "min",
  },
  {
    key: "user",
    header: "Persona bloqueada",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{personName(r.user, r.subject)}</div>
        <div className="text-2xs text-fg-subtle break-all">{personDetail(r.user, r.subject)}</div>
      </div>
    ),
  },
  {
    key: "category",
    header: "Categoría",
    cell: (r) => (
      <span className="text-fg text-xs">{INCIDENT_CATEGORY_LABELS[r.category]}</span>
    ),
  },
  {
    key: "expires",
    header: "Expira",
    cell: (r) => (
      <span className="text-fg-muted text-xs">
        {r.expiresAt ? formatDate(r.expiresAt) : "Perpetuo"}
      </span>
    ),
    width: "min",
  },
  {
    key: "appealable",
    header: "Apelable",
    cell: (r) => (
      <span className={r.appealable ? "nx-badge-info" : "nx-badge-neutral"}>
        {r.appealable ? "Sí" : "No"}
      </span>
    ),
    width: "min",
  },
  {
    key: "bannedAt",
    header: "Baneado",
    cell: (r) => <span className="text-fg-subtle text-xs">{formatDate(r.bannedAt)}</span>,
    width: "min",
    align: "right",
  },
];

function BanDrawer({ data, onClose }: { data: GlobalBan; onClose: () => void }) {
  const qc = useQueryClient();
  const { push } = useToast();

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const revoke = useMutation({
    mutationFn: (reason: string) => globalBansApi.revoke(data.id, reason),
    onSuccess: () => {
      push({ kind: "success", message: "Ban revocado. El usuario recupera acceso." });
      qc.invalidateQueries({ queryKey: ["governance", "global-bans"] });
      onClose();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || "No se pudo revocar." }),
  });

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[520px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="nx-h2 truncate">Ban global #{data.id}</h2>
            <p className="text-2xs text-fg-subtle mt-0.5">
              {INCIDENT_CATEGORY_LABELS[data.category]} · {formatDate(data.bannedAt)}
            </p>
          </div>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">
          <Section title="Persona">
            <Field label="Nombre" value={personName(data.user, data.subject)} wide />
            <Field
              label="Email"
              value={
                data.user?.email ? (
                  <a className="text-accent hover:underline" href={`mailto:${data.user.email}`}>
                    {data.user.email}
                  </a>
                ) : "—"
              }
              wide
            />
            <Field label="User ID" value={data.user?.id ?? "—"} />
            <Field label="RUT" value={rutTailLabel(data.subject) ?? "—"} />
          </Section>

          <Section title="Ban">
            <Field label="Categoría" value={INCIDENT_CATEGORY_LABELS[data.category]} />
            <Field label="Baneado el" value={formatDateTime(data.bannedAt)} />
            <Field
              label="Expira"
              value={data.expiresAt ? formatDateTime(data.expiresAt) : "Perpetuo"}
              wide
            />
            <Field label="Apelable" value={data.appealable ? "Sí" : "No"} />
            <Field label="Postulación origen" value={data.sourceProposal ? `#${data.sourceProposal.id}` : "—"} />
            <Field
              label="Motivo público (visto por el usuario)"
              value={data.publicReason ?? "—"}
              wide
            />
          </Section>
        </div>

        <footer className="px-5 py-3 border-t border-border bg-bg-surface flex flex-wrap items-center gap-2 justify-end">
          <div className="flex items-start gap-2 text-2xs text-fg-subtle mr-auto max-w-[300px]">
            <AlertTriangle size={12} className="text-warning shrink-0 mt-0.5" />
            <span>Revocar = el usuario recupera acceso a todos los clubes inmediatamente.</span>
          </div>
          <button
            onClick={async () => {
              const reason = await confirmAction({
                title: "Revocar ban global",
                message: `Se desbloquea a ${personName(data.user, data.subject)} en TODOS los clubes.`,
                promptLabel: "Motivo de la revocación (obligatorio)",
                confirmLabel: "Revocar",
                tone: "danger",
              });
              if (reason !== null && (reason as string).trim()) revoke.mutate((reason as string).trim());
            }}
            disabled={revoke.isPending}
            className="nx-btn-danger"
          >
            <RotateCcw size={14} /> Revocar ban
          </button>
        </footer>
      </aside>
    </>
  );
}
